// ─────────────────────────────────────────────────────────────────────────────
// src/i18n/index.js
//
// Zero-dependency i18n module. Provides the same API surface as i18next +
// react-i18next so existing call sites (HomePage, AdShell, anything using
// `useTranslation`, `i18n.changeLanguage`, `i18n.language`) work without
// changes other than the import path.
//
// Replace this file's internals with a real i18next setup later if/when you
// have the dependency budget — the API stays identical so consumers don't
// have to change.
//
// Features:
//   - Supported languages match SUPPORTED_LANGUAGES in @/lib/i18nRoutes
//   - Detection order: URL path → localStorage → navigator.languages → "en"
//   - RTL auto-applied for Hebrew via <html dir>
//   - localStorage persistence ("i18nextLng" key — same as i18next default,
//     so a future migration inherits the user's stored choice)
//   - Dot-notation key lookup (t("page.weather.title"))
//   - {{var}} interpolation (t("hello", { name: "Eran" }))
//   - Uses the selected language without falling back to English for missing keys
//   - React hook `useTranslation()` returning { t, i18n } (matches react-i18next)
// ─────────────────────────────────────────────────────────────────────────────

import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import en from "./locales/en.json";
import he from "./locales/he.json";
import es from "./locales/es.json";
import it from "./locales/it.json";
import fr from "./locales/fr.json";
import de from "./locales/de.json";
import pt from "./locales/pt.json";
import ar from "./locales/ar.json";
import el from "./locales/el.json";
import ru from "./locales/ru.json";
import tr from "./locales/tr.json";
import zh from "./locales/zh.json";
import { homeTranslations } from "@/lib/homeTranslations";
import { publicTranslationsReady } from "@/lib/publicTranslations";
import { getDailyBriefingTranslations } from "@/lib/dailyBriefingTranslations";
import { getForecastToolTranslations } from "@/lib/forecastToolTranslations";
import { getBillingTranslations, planTranslations } from "@/lib/billingTranslations";

export { getAffiliateTranslations } from "@/lib/affiliateTranslations";
export { getGlossaryTermLayoutLabels, getLocalizedGlossaryTermDetail } from "@/lib/glossaryTermTranslations";
export { getLearningArticleTitle } from "@/lib/learningArticleTitleTranslations";
export { getLogbookCopy } from "@/lib/logbookTranslations";
export { getProfileTranslations } from "@/lib/profileTranslations";
export { formatPremiumText, getPremiumTranslations } from "@/lib/premiumTranslations.js";
export { getPublicWeatherTranslations } from "@/lib/publicWeatherTranslations";
export { getLocalizedGlossaryLinks, getLocalizedSeoHub, getSeoHubLabels } from "@/lib/publicSeoHubTranslations";
export { getRegulationsExplorerText, getRegulationsHubTranslation, getRegulationZoneText, simpleCountryName } from "@/lib/regulationTranslations";
export { getTripPlannerCopy, TRIP_PLANNER_SUPPORTED_LANGUAGES } from "@/lib/tripPlannerTranslations";
export { getWireCopy, getWireFallbackArticle, getWireFallbackArticles } from "@/lib/wireTranslations";

const baseResources = { en, he, es, it, fr, de, pt, ar, el, ru, tr, zh };
const resources = Object.fromEntries(
  Object.entries(baseResources).map(([lang, locale]) => [
    lang,
    {
      ...locale,
      home: homeTranslations[lang] || {},
      public: publicTranslationsReady[lang] || {},
      dailyBriefing: getDailyBriefingTranslations(lang),
      forecastTools: getForecastToolTranslations(lang),
      billing: getBillingTranslations(lang),
      subscriptionPlans: planTranslations[lang] || {},
    },
  ])
);

export const SUPPORTED_LNGS = ["en", "he", "es", "it", "fr", "de", "pt", "ar", "el", "ru", "tr", "zh"];

export const RTL_LNGS = ["he", "ar"];

const DEFAULT_LNG = "en";
const STORAGE_KEYS = ["appLanguage", "marstick_locale", "i18nextLng"];

// ─── Detection ───────────────────────────────────────────────────────────────

function normalize(code) {
  if (!code) return null;
  const base = String(code).toLowerCase().split("-")[0];
  return SUPPORTED_LNGS.includes(base) ? base : null;
}

function detectInitialLanguage() {
  if (typeof window === "undefined") return DEFAULT_LNG;

  // 1. URL prefix /:lang/...
  const seg = window.location.pathname.split("/").filter(Boolean)[0];
  const fromPath = normalize(seg);
  if (fromPath) return fromPath;

  // 2. localStorage (only a user's previous explicit choice)
  try {
    if (window.localStorage?.getItem("marstick_locale_explicit") === "true") {
      for (const key of STORAGE_KEYS) {
        const fromStorage = normalize(window.localStorage.getItem(key));
        if (fromStorage) return fromStorage;
      }
    }
  } catch {
    // private mode / quota — ignore
  }

  // 3. navigator.languages / navigator.language
  if (typeof navigator !== "undefined") {
    const list =
      (Array.isArray(navigator.languages) && navigator.languages.length > 0
        ? navigator.languages
        : [navigator.language]) || [];
    for (const tag of list) {
      const code = normalize(tag);
      if (code) return code;
    }
  }

  return DEFAULT_LNG;
}

// ─── Translation lookup ──────────────────────────────────────────────────────

function lookupKey(dict, key) {
  if (!dict || !key) return undefined;
  // Dot notation: "page.weather.title" → dict.page.weather.title
  return key.split(".").reduce(
    (acc, k) => (acc != null ? acc[k] : undefined),
    dict
  );
}

function interpolate(str, vars) {
  if (!vars || typeof str !== "string") return str;
  return str.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, name) =>
    vars[name] != null ? String(vars[name]) : ""
  );
}

function resolveT(language, key, options) {
  const opts = options || {};
  const primary = lookupKey(resources[language], key);
  if (typeof primary === "string") return interpolate(primary, opts);
  if (primary !== undefined) return primary;
  if (typeof opts.defaultValue === "string") {
    return language === DEFAULT_LNG ? interpolate(opts.defaultValue, opts) : "";
  }
  if (opts.defaultValue !== undefined) return opts.defaultValue;
  return "";
}

// ─── Singleton i18n instance (matches i18next surface) ───────────────────────

const listeners = new Set();
let currentLng = detectInitialLanguage();

function applyHtmlAttrs(lng) {
  if (typeof document === "undefined") return;
  document.documentElement.lang = lng;
  document.documentElement.dir = RTL_LNGS.includes(lng) ? "rtl" : "ltr";
  if (RTL_LNGS.includes(lng)) {
    document.documentElement.classList.add("rtl");
  } else {
    document.documentElement.classList.remove("rtl");
  }
}

const i18n = {
  get language() {
    return currentLng;
  },

  // Read-only snapshot of the loaded resources, mirrors i18next API
  get languages() {
    return [...SUPPORTED_LNGS];
  },

  changeLanguage(lng) {
    const code = normalize(lng);
    if (!code || code === currentLng) {
      return Promise.resolve(currentLng);
    }
    currentLng = code;
    try {
      if (typeof window !== "undefined" && window.localStorage) {
        window.localStorage.setItem("appLanguage", code);
        window.localStorage.setItem("marstick_locale", code);
        window.localStorage.setItem("i18nextLng", code);
        window.localStorage.setItem("marstick_locale_explicit", "true");
        window.localStorage.removeItem("lang");
        window.localStorage.removeItem("language");
        window.localStorage.removeItem("locale");
      }
    } catch {
      // ignore
    }
    applyHtmlAttrs(code);
    listeners.forEach((fn) => {
      try { fn(code); } catch {}
    });
    return Promise.resolve(code);
  },

  t(key, options) {
    return resolveT(currentLng, key, options);
  },

  // Event API used by react-i18next consumers and our own hook
  on(event, cb) {
    if (event === "languageChanged" && typeof cb === "function") {
      listeners.add(cb);
    }
  },

  off(event, cb) {
    if (event === "languageChanged") {
      listeners.delete(cb);
    }
  },
};

// Apply initial <html lang>/<dir> synchronously on import
applyHtmlAttrs(currentLng);

// ─── React hook (drop-in for react-i18next's useTranslation) ─────────────────

export function getTranslationsForLanguage(language = DEFAULT_LNG, namespace) {
  const code = normalize(language) || DEFAULT_LNG;
  if (!namespace) return resources[code] || resources[DEFAULT_LNG];
  return resolveT(code, namespace, { defaultValue: {} });
}

export function useTranslation() {
  // Re-render the consuming component whenever the language changes.
  const [, setTick] = useState(currentLng);

  useEffect(() => {
    const handler = (lng) => setTick(lng);
    i18n.on("languageChanged", handler);
    return () => i18n.off("languageChanged", handler);
  }, []);

  // Memoize `t` so dependency arrays remain stable per language.
  const t = useMemo(
    () => (key, options) => resolveT(i18n.language, key, options),
    // Re-create t when language changes (setTick triggers re-render → fresh memo)
    [currentLng] // eslint-disable-line react-hooks/exhaustive-deps
  );

  return { t, i18n };
}

export default i18n;